// Framing for live file transfer (docs/crypto-spec.md §6): the manifest,
// progress and chunk record bodies, and the strict order chunks must
// arrive in. The records themselves are sealed by @sss/crypto; this only
// lays out and checks what's inside them. Everything received is
// untrusted, so anything unexpected is null (and ends the transfer).

export const CHUNK_BYTES = 64 * 1024;
/** The sender stays at most this far ahead of what the recipient has written (§6.3). */
export const WINDOW_BYTES = 16 * 1024 * 1024;
/** The recipient reports progress at least this often. */
export const PROGRESS_EVERY_BYTES = 4 * 1024 * 1024;
export const MAX_FILES = 100;
/** Through the room (no direct connection), only this much may be sent (§6.5, docs/adr/0012). ROOM_LIMITS.maxRelayChars fits one such transfer. */
export const RELAY_MAX_TOTAL_BYTES = 25 * 1024 * 1024;

const MAX_NAME_BYTES = 255;
const MAX_TYPE_CHARS = 255;
const CHUNK_HEADER_BYTES = 2 + 8;

export interface FileEntry {
  name: string;
  size: number;
  type: string;
}

const encoder = new TextEncoder();

export const totalBytes = (files: readonly FileEntry[]): number => files.reduce((sum, f) => sum + f.size, 0);

export const encodeManifest = (files: readonly FileEntry[]): Uint8Array =>
  encoder.encode(JSON.stringify({ files: files.map(({ name, size, type }) => ({ name, size, type })) }));

export const parseManifest = (body: Uint8Array): FileEntry[] | null => {
  let value: unknown;
  try {
    value = JSON.parse(new TextDecoder('utf-8', { fatal: true }).decode(body));
  } catch {
    return null;
  }
  const files = (value as { files?: unknown } | null)?.files;
  if (!Array.isArray(files) || files.length === 0 || files.length > MAX_FILES) return null;
  const entries: FileEntry[] = [];
  for (const file of files as unknown[]) {
    if (typeof file !== 'object' || file === null) return null;
    const { name, size, type } = file as Record<string, unknown>;
    if (typeof name !== 'string' || name.length === 0 || encoder.encode(name).length > MAX_NAME_BYTES) return null;
    if (typeof size !== 'number' || !Number.isSafeInteger(size) || size < 0) return null;
    if (typeof type !== 'string' || type.length > MAX_TYPE_CHARS) return null;
    entries.push({ name, size, type });
  }
  return Number.isSafeInteger(totalBytes(entries)) ? entries : null;
};

/** u64 BE, for values up to 2^53 − 1. */
const writeU64 = (view: DataView, at: number, value: number): void => {
  view.setUint32(at, Math.floor(value / 2 ** 32));
  view.setUint32(at + 4, value >>> 0);
};

const readU64 = (view: DataView, at: number): number | null => {
  const value = view.getUint32(at) * 2 ** 32 + view.getUint32(at + 4);
  return Number.isSafeInteger(value) ? value : null;
};

export const encodeProgress = (bytesWritten: number): Uint8Array => {
  const body = new Uint8Array(8);
  writeU64(new DataView(body.buffer), 0, bytesWritten);
  return body;
};

export const parseProgress = (body: Uint8Array): number | null =>
  body.length === 8 ? readU64(new DataView(body.buffer, body.byteOffset, body.byteLength), 0) : null;

export interface Chunk {
  fileIndex: number;
  offset: number;
  data: Uint8Array;
}

export const encodeChunk = (fileIndex: number, offset: number, data: Uint8Array): Uint8Array => {
  const body = new Uint8Array(CHUNK_HEADER_BYTES + data.length);
  const view = new DataView(body.buffer);
  view.setUint16(0, fileIndex);
  writeU64(view, 2, offset);
  body.set(data, CHUNK_HEADER_BYTES);
  return body;
};

export const parseChunk = (body: Uint8Array): Chunk | null => {
  if (body.length <= CHUNK_HEADER_BYTES || body.length > CHUNK_HEADER_BYTES + CHUNK_BYTES) return null;
  const view = new DataView(body.buffer, body.byteOffset, body.byteLength);
  const offset = readU64(view, 2);
  if (offset === null) return null;
  return { fileIndex: view.getUint16(0), offset, data: body.subarray(CHUNK_HEADER_BYTES) };
};

/**
 * The recipient's check that chunks arrive exactly in order (§6.4): each
 * must be the next bytes of the current file, never past its size. Empty
 * files are skipped, since they have no chunks.
 */
export class ChunkSequence {
  private fileIndex = 0;
  private offset = 0;
  received = 0;

  constructor(private readonly files: readonly FileEntry[]) {
    this.skipFinished();
  }

  get done(): boolean {
    return this.fileIndex >= this.files.length;
  }

  /** Whether `chunk` is the next one; if so, it's counted. */
  accept(chunk: Chunk): boolean {
    const file = this.files[this.fileIndex];
    if (!file || chunk.fileIndex !== this.fileIndex || chunk.offset !== this.offset) return false;
    if (chunk.offset + chunk.data.length > file.size) return false;
    this.offset += chunk.data.length;
    this.received += chunk.data.length;
    this.skipFinished();
    return true;
  }

  private skipFinished(): void {
    while (this.fileIndex < this.files.length && this.offset >= (this.files[this.fileIndex] as FileEntry).size) {
      this.fileIndex += 1;
      this.offset = 0;
    }
  }
}
